import { useMemo } from 'react'

import { useNavigate } from 'react-router-dom'
import { createColumnHelper } from '@tanstack/react-table'

import { AirdropItem, AirdropList } from 'types/entities'

import { useIsWrongChainId } from 'hooks/wallet'

import { useRecipientsList, useSetRecipientsList, useToken } from 'state/airdrop/hooks'

import { fromWei } from 'utils/crypto'
import { errors } from 'constants/errors'
import { getTokenSubLink, Links, TokenSubLinks } from 'constants/link'

import { Button, Notification, Slash, Table } from 'components/ui'

import { Amount, LabelTitle, TableWrap, ButtonWrap } from './Style'

type Props = {
  exceptions: AirdropList
  isEmulationFailed: boolean
}

export function Exceptions({ exceptions, isEmulationFailed }: Props) {
  const token = useToken()
  const navigate = useNavigate()
  const isWrongChainId = useIsWrongChainId()

  const recipientsList = useRecipientsList()
  const setRecipientsList = useSetRecipientsList()

  const columnHelper = createColumnHelper<AirdropItem>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('addr', {
        header: 'address',
        size: 400,
      }),
      columnHelper.accessor('amount', {
        header: 'amount',
        cell: (data) => {
          return (
            <Amount>
              {fromWei(data.getValue().toString(), token?.decimals)} {token?.symbol}
            </Amount>
          )
        },
        size: 200,
      }),
    ],
    [token, exceptions],
  )

  const onNextPage = () => {
    const recipientsListWithoutExceptions = recipientsList.filter(
      (recipient) => recipient.addr !== exceptions.find((exc) => exc.addr === recipient.addr)?.addr,
    )

    setRecipientsList(recipientsListWithoutExceptions)

    navigate(getTokenSubLink(TokenSubLinks.AIRDROP))
  }

  const backToPrepare = () => {
    navigate(Links.TOKEN)
  }

  return (
    <>
      <LabelTitle>
        <Slash />
        _list-of-bad-addresses
      </LabelTitle>

      <TableWrap>
        <Table<AirdropItem> data={exceptions} name='exceptionsTable' columns={columns} />
      </TableWrap>

      {isEmulationFailed && (
        <>
          <Notification type='warning' message={errors.validation.EMULATION_FAILED} />
          <br />
        </>
      )}

      <ButtonWrap isWrapped={isEmulationFailed}>
        <Button fullSize type='outline' onClick={backToPrepare}>
          _back-to-previous-step
        </Button>

        {!isEmulationFailed && (
          <Button fullSize onClick={onNextPage} disabled={isWrongChainId}>
            _continue-without-bad-addresses
          </Button>
        )}
      </ButtonWrap>
    </>
  )
}
